import React, { useEffect, useState, useRef } from 'react';
import { Spin, Radio, Toast } from '@douyinfe/semi-ui';
import { API, apiErrMsg, pageVisible } from '../helpers';
// 速率/已用时的格式化与「令牌管理」页共用 —— 两边显示必须一模一样
import { fmtTps, fmtElapsed, liveStatusText } from '../helpers/speed';
import { navigate } from '../router';

// 统计卡片。窄屏下字号自动缩小,避免数字撑破卡片。
// 用 CSS clamp 依据视口宽度缩放:桌面 22px,手机最小 15px。
const Stat = ({ label, value, suffix }) => (
  <div style={{
    background: '#fff',
    padding: '16px 12px',
    borderRadius: 10,
    border: '1px solid #e5e6eb',
    minWidth: 0,
    overflow: 'hidden',
  }}>
    <div style={{ fontSize: 12, color: '#86909c', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
    <div style={{
      fontSize: 'clamp(15px, 4.6vw, 22px)',
      fontWeight: 600,
      marginTop: 4,
      color: '#1f2329',
      lineHeight: 1.25,
      // 数字过长时压缩字距并允许断行,保证不溢出卡片
      wordBreak: 'break-all',
    }}>
      {typeof value === 'number' ? value.toLocaleString() : value}
      {suffix && <span style={{ fontSize: 'clamp(11px, 3vw, 13px)', color: '#86909c', marginLeft: 4 }}>{suffix}</span>}
    </div>
  </div>
);

// 窄屏(手机)判断:跟 AdminChannels / AdminPlans / AdminTokens 同一套写法(768 断点)。
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < breakpoint);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

// 速度列的配色:按**本次请求生效的速度比例**(限速因子)判,不用瞬时比值。
// 瞬时比值(实际÷上游)在"上游正在提速"时会失真 —— 上游刚加速、网关的
// 平均速率基准还没跟上,比值会掉到 20% 这种假象,而实际限速比例可能还有 80%。
// factor 是网关自己算出来的限速结果,不会骗人。
function factorColor(f) {
  if (f == null || f >= 0.9) return '#1f2329';
  return f >= 0.6 ? '#ff7d00' : '#f56565';
}

// 中文星期几
function weekdayLabel(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const names = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
  return names[d.getDay()];
}

// 日期键统一按北京时间(UTC+8)切 —— 必须和后端 SQL 的 AT TIME ZONE 'Asia/Shanghai' 完全一致。
// 之前后端按 UTC 分组、前端按本地时间取"今天",两边错开 8 小时,
// 结果当天的实时调用被算到前一天的柱子里,最后一根(今天)反而不显示。
const CN_TZ_OFFSET_MS = 8 * 3600 * 1000;
const dayKey = (d) => new Date(d.getTime() + CN_TZ_OFFSET_MS).toISOString().slice(0, 10);

// 7 天趋势柱状图(支持切换 calls / quota / success_rate)
const TrendBars = ({ byDay, metric }) => {
  const [hover, setHover] = useState(null);
  const raw = Array.isArray(byDay) ? byDay : [];
  // 补齐最近 7 天:缺数据的日期占位(值=0),保持柱图始终 7 根,布局稳定。
  // 往前推用"实时钟"减 24 小时,中国无夏令时,每次减一天正好跨一个日历日。
  const list = [];
  const map = new Map(raw.map((d) => [d.day, d]));
  for (let i = 6; i >= 0; i--) {
    const key = dayKey(new Date(Date.now() - i * 86400000));
    const found = map.get(key);
    list.push(found || { day: key, calls: 0, quota: 0, success_calls: 0, success_rate: null });
  }
  const values = list.map((d) => {
    if (metric === 'quota') return Number(d.quota || 0);
    if (metric === 'success_rate') return Number(d.success_rate || 0);
    return Number(d.calls || 0);
  });
  const max = Math.max(1, ...values);
  const colorMap = {
    calls:        { bar: '#22c55e', soft: '#dcfce7' },
    quota:        { bar: '#3b82f6', soft: '#dbeafe' },
    success_rate: { bar: '#10b981', soft: '#d1fae5' },
  };
  const color = colorMap[metric] || colorMap.calls;
  const todayKey = dayKey(new Date());
  const fmtVal = (v) => metric === 'success_rate' ? v.toFixed(1) + '%' : v.toLocaleString();
  const unit = metric === 'quota' ? 'quota' : metric === 'success_rate' ? '%' : '次';
  // 柱体宽度:7 根均分,按容器宽度自适应
  const barMaxWidth = 56;
  const barMinWidth = 16;
  const barWidth = Math.max(barMinWidth, Math.min(barMaxWidth, 70)); // 70% 子列宽

  return (
    <div style={{ position: 'relative' }}>
      {/* 单位标签:居中显示在柱图上方 */}
      <div style={{ textAlign: 'center', fontSize: 11, color: '#86909c', marginBottom: 6 }}>
        单位:{unit} · 7 天滚动窗口 · 按北京时间切日 · 最后一根为今天(实时累加) · 悬停查看明细 · 无数据日灰色占位
      </div>
      {list.every((d) => (metric === 'quota' ? !d.quota : metric === 'success_rate' ? d.success_rate == null : !d.calls)) ? (
        <div style={{ color: '#c9cdd4', textAlign: 'center', padding: 30 }}>暂无数据</div>
      ) : (
        // 统一坐标系:
        //   绘图区(plot area): 高度 120px,柱体在这范围内
        //   X 轴文字区(x-axis labels): 高度 ~36px,值/日期/周几 三行
        //   Y 轴容器高度 = 120 + 36 = 156(覆盖绘图区+标签)
        //   "0" 标签 top=120(对齐绘图区底边 + 标签顶部间距 4px)
        <div style={{ display: 'flex', padding: '4px 0 0' }}>
          {/* Y 轴刻度 */}
          <div style={{ position: 'relative', width: 52, flexShrink: 0, height: 156 }}>
            {[max, Math.round(max * 0.5), 0].map((v, i) => (
              <div key={i} style={{
                position: 'absolute', right: 6, fontSize: 10, color: '#a3a8b3',
                lineHeight: '12px',
                // 三刻度数字精确落在绘图区(120px)三个位置:
                //   max → 文字底部对齐 0px(顶端) → top:0 + translateY(0),数字向下展开到 12px
                //   max/2 → 文字中心对齐 60px → top:60 + translateY(-50%)
                //   0 → 文字顶部对齐 120px(底端) → top:120 + translateY(-100%),数字底部在 120
                top: i === 0 ? 0 : i === 1 ? 60 : 120,
                transform: i === 0 ? 'translateY(0)' : i === 1 ? 'translateY(-50%)' : 'translateY(-100%)',
              }}>{fmtVal(v)}</div>
            ))}
          </div>

          {/* 右侧整列:绘图区 + X 轴标签 */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* 绘图区 120px 高 */}
            <div style={{ position: 'relative', height: 120, borderLeft: '1px solid #eef0f3', borderBottom: '1px solid #eef0f3' }}>
              {/* 网格线 */}
              {[0, 60, 120].map((y) => (
                <div key={y} style={{
                  position: 'absolute', left: 0, right: 0, top: (120 - y) + 'px',
                  borderTop: y === 0 ? 'none' : '1px dashed #f0f1f3',
                  pointerEvents: 'none',
                }} />
              ))}

              <div style={{ display: 'flex', alignItems: 'flex-end', height: 120, gap: 0, padding: '0 12px' }}>
                {list.map((d, i) => {
                  const v = values[i];
                  const h = Math.max(0, (v / max) * 120);
                  const isToday = d.day === todayKey;
                  const isHover = hover === i;
                  const hasData = (metric === 'quota' ? d.quota > 0 : metric === 'success_rate' ? d.success_rate != null : d.calls > 0);
                  return (
                    <div key={d.day}
                      onMouseEnter={() => setHover(i)}
                      onMouseLeave={() => setHover(null)}
                      style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%', position: 'relative', cursor: hasData ? 'pointer' : 'default' }}
                    >
                      {isHover && hasData && (
                        <div style={{
                          position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
                          background: 'rgba(31,35,41,0.92)', color: '#fff', padding: '6px 10px', borderRadius: 6,
                          fontSize: 12, whiteSpace: 'nowrap', zIndex: 10, pointerEvents: 'none', marginBottom: 4,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        }}>
                          <div style={{ fontWeight: 600 }}>
                            {d.day} {weekdayLabel(d.day)}{isToday ? ' · 今天(实时)' : ''}
                          </div>
                          <div>调用: {d.calls || 0} 次</div>
                          <div>quota: {d.quota || 0}</div>
                          <div>成功率: {d.success_rate == null ? '—' : d.success_rate + '%'}</div>
                        </div>
                      )}
                      <div style={{
                        width: barWidth + '%', maxWidth: barMaxWidth, minWidth: barMinWidth,
                        height: h + 'px',
                        background: !hasData ? '#eef0f3' : (isHover ? color.bar : (isToday ? color.bar : color.bar + 'cc')),
                        borderRadius: '4px 4px 0 0',
                        transition: 'all 0.15s',
                        opacity: hover != null && !isHover ? 0.5 : 1,
                      }} />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* X 轴文字区(独立 flex,与绘图区等宽均分 7 列) */}
            <div style={{ display: 'flex', padding: '4px 12px 0' }}>
              {list.map((d, i) => {
                const v = values[i];
                const isToday = d.day === todayKey;
                const hasData = (metric === 'quota' ? d.quota > 0 : metric === 'success_rate' ? d.success_rate != null : d.calls > 0);
                return (
                  <div key={d.day}
                    style={{ flex: 1, minWidth: 0, textAlign: 'center' }}
                  >
                    <div style={{ fontSize: 11, color: isToday ? '#1d7dfa' : (hasData ? '#52525b' : '#c9cdd4'), fontWeight: isToday ? 600 : 400 }}>
                      {hasData ? fmtVal(v) : '—'}
                    </div>
                    <div style={{ fontSize: 10, color: isToday ? '#1d7dfa' : '#86909c', marginTop: 2 }}>
                      {d.day.slice(5)}
                    </div>
                    <div style={{ fontSize: 9, color: isToday ? '#1d7dfa' : '#c9cdd4', marginTop: 2 }}>
                      {isToday ? '今天' : weekdayLabel(d.day)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 剩余时长:上游给的是毫秒,转成人话
function fmtRemainMs(ms) {
  const v = Number(ms || 0);
  if (!v || v <= 0) return '已用完';
  if (v < 3600000) return '剩 ' + Math.round(v / 60000) + ' 分钟';
  if (v < 48 * 3600000) return '剩 ' + (v / 3600000).toFixed(1) + ' 小时';
  return '剩 ' + (v / 86400000).toFixed(1) + ' 天';
}

function fmtWindow(start, end) {
  if (!start || !end) return '—';
  const f = (t) => {
    const d = new Date(t);
    const p = (n) => String(n).padStart(2, '0');
    return (d.getMonth() + 1) + '/' + d.getDate() + ' ' + p(d.getHours()) + ':' + p(d.getMinutes());
  };
  return f(start) + ' → ' + f(end);
}

// 剩余百分比的颜色:>=50% 绿,>=20% 橙,更低红(0% = 已耗尽)
function pctColor(pct) {
  if (pct == null) return '#c9cdd4';
  if (pct <= 0) return '#f56565';
  if (pct >= 50) return '#00b42a';
  if (pct >= 20) return '#ff7d00';
  return '#f56565';
}

// 渠道面板:一个表看全每个渠道
//   · 上游官方配额(5h 窗口 / 周窗口剩余)—— 走上游接口,后端缓存 2 分钟
//   · 并发占用(在飞 / 排队)与 24h 调用 —— 本地实时,5 秒刷新
// 手动点刷新只用于"强制重查上游配额"(上游配额本身变化慢,自动轮询已够)。
const ChannelUsagePanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  // 列宽:桌面固定(1080 合计),手机同一套列压窄一档(634 合计)。
  // 手机上**一列不减**,只是每列更窄 + 字号 11px + 内边距收紧,
  // 横向滚动距离比桌面少 41%,渠道名的省略号仍然生效。
  const isMobile = useIsMobile();
  const cw = isMobile
    ? { id: 34, name: 118, dur: 78, pct: 58, conc: 88, calls: 66, status: 56 }
    : { id: 60, name: 180, dur: 135, pct: 95, conc: 140, calls: 125, status: 115 };
  const pad = isMobile ? '5px 4px' : '6px 8px';
  const minWidth = isMobile ? 634 : 1080;

  // 请求序号:5 秒轮询和手动「重查配额」可能同时在飞,
  // 慢的旧响应后到会覆盖新数据(手动重查后看到的还是 2 分钟前的旧快照)。
  // 只接受最后一次请求的响应。
  const usageSeq = useRef(0);

  const load = (force) => {
    if (force) setLoading(true);
    const seq = ++usageSeq.current;
    API.get('/api/admin/stats/channel-usage' + (force ? '?refresh=1' : ''))
      .then((r) => {
        if (seq !== usageSeq.current) return; // 有更新的请求在飞,丢弃过期响应
        setData(r.data.data);
        setErr('');
      })
      .catch((e) => { if (seq === usageSeq.current) setErr(apiErrMsg(e)); })
      .finally(() => { if (seq === usageSeq.current) setLoading(false); });
  };

  // 自动刷新:本地实时项(并发占用/24h 调用)每 5 秒更新一次
  // 页面不可见(切到别的标签页/最小化)时跳过 —— 没人看的时候没必要一直打接口。
  useEffect(() => {
    load(false);
    const id = setInterval(() => { if (!pageVisible()) return; load(false); }, 5000);
    return () => clearInterval(id);
  }, []);

  // 1 秒心跳:让「剩余时长」每秒递减。
  // 为什么需要:上游配额是 2 分钟缓存的**快照**,不重新查上游它就不变 ——
  // 直接显示上游给的 remain_ms,倒计时会卡住不动(看着像坏了)。
  // 窗口结束时间是绝对时间戳,本地按当前时间算就得到真实倒计时。
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const rows = data?.channels || [];
  // 上游配额的最后查询时间(各渠道可能不同,取最早的作为整表时间)。
  // 注意:不能把 Date.now() 一起塞进 min —— 那样没有任何渠道带时间戳时
  // 会显示成"刚刚更新",等于谎报新鲜度。
  const usageStamps = rows.map((c) => c.usage_fetched_at || 0).filter(Boolean);
  const usageAt = usageStamps.length ? Math.min(...usageStamps) : 0;

  return (
    <div style={{ background: '#fff', borderRadius: 10, padding: 16, border: '1px solid #e5e6eb', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }} title="每个渠道一行:上游官方配额(5h/周剩余时长,悬停看具体窗口)+ 本地实时占用。并发占用与 24h 调用每 5 秒自动刷新;上游配额每 2 分钟才查一次,剩余时长本地实时倒数。">
          渠道用量(实时,5 秒刷新)
        </h3>
        <span style={{ fontSize: 12, color: '#86909c', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span title="在飞 = 全站在册的请求数(含排队等闸门、等渠道空位的);占槽 = 真正占着上游槽的请求数。两者与下表的「并发占用」来自同一次采样,所以永远对得上。">
            在飞 {data?.inflight_total ?? 0} · 占槽 {rows.reduce((s, c) => s + (c.inflight || 0), 0)}
          </span>
          <span title="上游配额:每 2 分钟才查一次上游,剩余时长本地实时倒数,配额百分比是 2 分钟前的快照。并发占用 / 24h 调用是本地实时,每 5 秒刷新一次。">
            上游配额每 2 分钟(更新于 {usageAt ? new Date(usageAt).toLocaleTimeString('zh-CN', { hour12: false }) : '—'})
          </span>
          <button
            id="channel-usage-refresh"
            type="button"
            onClick={() => load(true)}
            disabled={loading}
            style={{
              background: 'none', border: 'none', padding: 0,
              color: '#1d7dfa', fontSize: 12, cursor: loading ? 'default' : 'pointer',
              // 窄屏下别把"重查配额"折成两行
              whiteSpace: 'nowrap', flexShrink: 0,
            }}
          >
            {loading ? '查询中…' : '重查配额'}
          </button>
        </span>
      </div>

      {err ? (
        <div style={{ color: '#f56565', fontSize: 13 }}>查询失败: {err}</div>
      ) : !rows.length ? (
        <div style={{ color: '#c9cdd4', textAlign: 'center', padding: 20, fontSize: 13 }}>
          {loading ? '正在查询上游用量…' : '没有渠道'}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          {/* tableLayout: fixed —— 不定死列宽的话,"渠道"列(没有 width)会把
              表格剩下的所有空间都吃掉,变得又宽又空。定死宽度后各列比例稳定。
              手机端**不改成自适应**(自适应会让渠道名的省略号失效、内容撑破表格),
              而是同一套列再压窄一档:一列不减,表格总宽从 1065 降到 614,
              横向滚动距离少 42%,配合 11px 字号在手机上能多看出一列。 */}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: isMobile ? 11 : 12, minWidth, tableLayout: 'fixed' }}>
            <thead>
              <tr style={{ color: '#86909c', textAlign: 'left' }}>
                <th style={{ padding: pad, width: cw.id }}>ID</th>
                <th style={{ padding: pad, width: cw.name }}>渠道</th>
                <th style={{ padding: pad, width: cw.dur, textAlign: 'right' }}
                  title="距当前 5h 窗口重置还有多久。鼠标悬停看具体窗口起止时间。">
                  5h 剩余时长
                </th>
                <th style={{ padding: pad, width: cw.pct, textAlign: 'right' }}
                  title="当前 5h 窗口内还剩多少配额。">
                  5h 剩余
                </th>
                <th style={{ padding: pad, width: cw.dur, textAlign: 'right' }}
                  title="距当前周窗口重置还有多久。鼠标悬停看具体窗口起止时间。">
                  周剩余时长
                </th>
                <th style={{ padding: pad, width: cw.pct, textAlign: 'right' }}
                  title="当前周窗口内还剩多少配额。">
                  周剩余
                </th>
                <th style={{ padding: pad, width: cw.conc, textAlign: 'right' }}
                  title="占用 = 真正占着上游槽的请求数;「排」= 在等这个渠道空位的请求数。与表头的「在飞 / 占槽」是同一次采样,不会对不上。">
                  并发占用
                </th>
                <th style={{ padding: pad, width: cw.calls, textAlign: 'right' }}>24h 调用</th>
                <th style={{ padding: pad, width: cw.status, textAlign: 'right' }}>状态</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const p = c.primary;
                const iv = p?.interval || null;
                const wk = p?.weekly || null;
                const otherModels = (c.models_detail || []).filter((m) => m.model && m.model !== p?.model);
                const cellTitle = [
                  c.type + (p?.model ? ' · ' + p.model : ''),
                  c.enabled ? '' : '已停用',
                  c.base_url,
                  otherModels.length ? '上游还返回了其它模型配额: ' + otherModels.map((m) => m.model).join(', ') : '',
                ].filter(Boolean).join('\n');
                // 并发占用:在飞/上限,满了标红;排队数单列
                const max = c.max_concurrency || 0;
                const full = max > 0 && c.inflight >= max;
                // 剩余时长单元格:距窗口重置还有多久。
                // 具体窗口(起止时间)不常驻显示,放 tooltip 里,鼠标悬停才看。
                const remainMsCell = (w, label) => {
                  if (!c.supported) return <span style={{ color: '#c9cdd4' }}>—</span>;
                  if (c.error) return <span style={{ color: '#f56565' }} title={c.error}>查询失败</span>;
                  if (!w) return <span style={{ color: '#c9cdd4' }}>—</span>;
                  // 真实倒计时:窗口结束时间是绝对时间戳,本地按 now 实时算。
                  // 不能直接用上游给的 remain_ms —— 那是 2 分钟前的快照,会卡住。
                  let ms = 0;
                  if (w.end > 0) {
                    ms = Math.max(0, w.end - Date.now());
                  } else {
                    // 兜底:没有 end 时,用快照 remain_ms 减去缓存已过的时间
                    const elapsed = Date.now() - (c.usage_fetched_at || Date.now());
                    ms = Math.max(0, (w.remain_ms || 0) - elapsed);
                  }
                  const text = ms > 0 ? fmtRemainMs(ms) : '已重置';
                  const tip = [
                    label + '窗口:' + fmtWindow(w.start, w.end),
                    '距重置:' + text,
                    '剩余配额:' + (w.remain_pct != null ? w.remain_pct + '%' : '—') + '(上游快照,每 2 分钟查一次)',
                    w.status_text || '',
                  ].filter(Boolean).join('\n');
                  return (
                    <span style={{ display: 'inline-block', whiteSpace: 'nowrap' }} title={tip}>
                      {text}
                    </span>
                  );
                };
                // 剩余配额单元格:当前窗口内还剩多少百分比
                const remainPctCell = (w) => {
                  if (!c.supported || c.error || !w || w.remain_pct == null) {
                    return <span style={{ color: '#c9cdd4' }}>—</span>;
                  }
                  return <span style={{ color: pctColor(w.remain_pct) }}>{w.remain_pct + '%'}</span>;
                };
                return (
                  <tr key={c.id} style={{ borderTop: '1px solid #f0f1f3' }}>
                    <td style={{ padding: pad, color: '#86909c', whiteSpace: 'nowrap' }}>{c.id}</td>
                    <td style={{ padding: pad, whiteSpace: 'nowrap', color: '#1f2329', overflow: 'hidden', textOverflow: 'ellipsis' }} title={cellTitle}>{c.name}</td>
                    {/* 5h 剩余时长(窗口时间在 tooltip) */}
                    <td style={{ padding: pad, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {remainMsCell(iv, '5h')}
                    </td>
                    {/* 5h 剩余配额 */}
                    <td style={{ padding: pad, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {remainPctCell(iv)}
                    </td>
                    {/* 周剩余时长(窗口时间在 tooltip) */}
                    <td style={{ padding: pad, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {remainMsCell(wk, '周')}
                    </td>
                    {/* 周剩余配额 */}
                    <td style={{ padding: pad, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {remainPctCell(wk)}
                    </td>
                    {/* 并发占用(实时)——占用与「在飞 N」取自同一次采样 */}
                    <td style={{ padding: pad, textAlign: 'right', whiteSpace: 'nowrap' }}
                      title={
                        '占用 = 真正占着上游槽的请求数;「排」= 在等这个渠道空位的请求数。\n' +
                        '两者与表头的「在飞 / 占槽」来自同一次采样,所以不会对不上。'
                      }>
                      <span style={{ color: full ? '#f56565' : '#1f2329', fontWeight: full ? 600 : 400 }}>
                        {c.inflight}{max > 0 ? ' / ' + max : ''}
                      </span>
                      {c.queue > 0 && (
                        <span style={{ color: '#ff7d00', fontSize: 11, marginLeft: 6 }} title="排队等待空位的请求数">排 {c.queue}</span>
                      )}
                    </td>
                    {/* 24h 调用 */}
                    <td style={{ padding: pad, textAlign: 'right', whiteSpace: 'nowrap', color: '#1f2329' }}>
                      {c.calls_24h}
                    </td>
                    <td style={{ padding: pad, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {!c.enabled ? <span style={{ color: '#86909c' }}>禁用</span>
                        : !c.supported ? '—'
                        : c.error ? <span style={{ color: '#f56565' }}>查询失败</span>
                        : iv?.status_text === '已耗尽'
                          ? <span style={{ color: '#f56565' }}>已耗尽</span>
                          : <span style={{ color: '#00b42a' }}>正常</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const AdminStats = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gwOnline, setGwOnline] = useState(null);
  const [trendMetric, setTrendMetric] = useState('calls');
  const [conc, setConc] = useState(null);
  // 趋势图数据独立轮询(by_day),让"今天"这根柱子实时增长
  const [byDay, setByDay] = useState(null);

  useEffect(() => {
    API.get('/api/admin/stats/overview')
      .then((r) => { setData(r.data.data); setByDay(r.data.data.by_day); })
      .catch((e) => Toast.error(apiErrMsg(e)))
      .finally(() => setLoading(false));
  }, []);

  // 趋势图轮询:让"今天"这根柱子实时增长。
  // 走独立接口 /api/admin/stats/trend —— 只跑一次 7 天聚合(实测 ~70ms),
  // 不带 overview 里那些总量子查询,10 秒一次完全无压力。
  useEffect(() => {
    const poll = () => {
      if (!pageVisible()) return;
      API.get('/api/admin/stats/trend')
        .then((r) => setByDay(r.data.data.by_day))
        .catch(() => {});
    };
    const id = setInterval(poll, 10000);
    return () => clearInterval(id);
  }, []);

  // 并发观测:渠道槽 / 用户槽的实时占用,5 秒一次
  useEffect(() => {
    const poll = () => {
      if (!pageVisible()) return;
      API.get('/api/admin/stats/concurrency')
        .then((r) => setConc(r.data.data))
        .catch(() => {});
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const poll = () => {
      if (!pageVisible()) return;
      fetch('/api/status').then((r) => r.json()).then((j) => {
        setGwOnline(j?.data?.online ?? null);
      }).catch(() => setGwOnline(false));
    };
    poll();
    const id = setInterval(poll, 30000);
    return () => clearInterval(id);
  }, []);

  if (loading) return <Spin spinning />;
  if (!data) return <div>暂无数据</div>;

  return (
    <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 16px' }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>网关总览</h2>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12,
                        color: gwOnline === null ? '#86909c' : (gwOnline ? '#00b42a' : '#f56565') }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%',
                          background: gwOnline === null ? '#c9cdd4' : (gwOnline ? '#22c55e' : '#f56565') }} />
          {gwOnline === null ? '检测中' : (gwOnline ? '网关在线' : '网关离线')}
        </span>
      </div>

      {/* 顶部 4 个"门面数字":门面价值 + 最近的用量健康度。
          已启用套餐 / 待售令牌 / 24h 消耗 quota / 7d 调用 全部撤掉 ——
          前两个是库存概念(平时不看),后两个在下面「7 天趋势」里能按指标切出来。 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 16 }}>
        <Stat label="令牌总金额" value={`¥${Number(data.total.tokens_value ?? 0).toFixed(1)}`} />
        <Stat label="24h 活跃 token" value={Number(data.total.tokens_active_24h ?? 0)} />
        <Stat label="24h 调用" value={Number(data.last_24h?.calls ?? 0)} />
        <Stat
          label="24h 成功率"
          value={data.last_24h?.success_rate == null ? '—' : Number(data.last_24h.success_rate).toFixed(2)}
          suffix={data.last_24h?.success_rate == null ? '' : '%'}
        />
      </div>

      <div style={{ background: '#fff', borderRadius: 10, padding: 16, border: '1px solid #e5e6eb', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <h3
            style={{ margin: 0, fontSize: 14, fontWeight: 600 }}
            title="只显示本机网关进程正在处理的请求。在跑 = 已拿到并发槽;排队 = 还在等本卡的并发闸门;占槽 = 真正占着上游槽的请求数(与「渠道用量」表的「占用」同口径、同一次采样);渠道排队 = 在等某个渠道的空位。"
          >
            在飞请求(实时,5 秒刷新)
          </h3>
          <span style={{ fontSize: 12, color: '#86909c' }}>
            {conc
              ? `在跑 ${(conc.inflight || []).filter((e) => e.phase !== 'queued').length}`
                + ` · 排队 ${(conc.inflight || []).filter((e) => e.phase === 'queued').length}`
                // 占槽 = 真正占着上游槽的请求数(与「渠道用量」表的「占用」同口径、同一次采样)
                + ` · 占槽 ${(conc.channels || []).reduce((s, c) => s + (c.inflight || 0), 0)}`
                + ` · 渠道排队 ${(conc.channels || []).reduce((s, c) => s + (c.queue || 0), 0)}`
                + ` · ${conc.scale?.tokens || 0} 张启用令牌`
              : '加载中…'}
          </span>
        </div>
        {!conc ? (
          <div style={{ color: '#c9cdd4', textAlign: 'center', padding: 20 }}>加载中…</div>
        ) : (conc.inflight || []).length === 0 ? (
          <div style={{ color: '#c9cdd4', textAlign: 'center', padding: 24, fontSize: 13 }}>
            当前没有请求在飞
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 560 }}>
              <thead>
                <tr style={{ color: '#86909c', textAlign: 'left' }}>
                  <th style={{ padding: '6px 8px' }}>模型</th>
                  <th style={{ padding: '6px 8px' }} title="点击令牌可跳到该令牌的使用记录">令牌</th>
                  <th style={{ padding: '6px 8px' }}>套餐</th>
                  <th style={{ padding: '6px 8px' }}>渠道</th>
                  <th
                    style={{ padding: '6px 8px', textAlign: 'right' }}
                    title="客户端实际收到的输出速度(t/s),已含降速效果。hover 看上游原速对比。token 数按字符估算,误差约 ±20%"
                  >
                    速度
                  </th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>已用时</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>状态</th>
                  <th
                    style={{ padding: '6px 8px', textAlign: 'right' }}
                    title="上游正在做深度思考时,正文一个字都还没吐出来。这段时间上游速率很高,但用户体感是「卡住」—— 不标出来会被误读成上游变慢"
                  >
                    思考
                  </th>
                </tr>
              </thead>
              <tbody>
                {conc.inflight.map((e) => (
                  <tr key={e.id} style={{ borderTop: '1px solid #f0f1f3' }}>
                    <td style={{ padding: '6px 8px', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{e.model || '—'}</td>
                    <td style={{ padding: '6px 8px', fontFamily: 'monospace', whiteSpace: 'nowrap', color: '#1f2329' }}>
                      {e.key_masked ? (
                        e.token_id ? (
                          <a
                            href={`/admin/tokens/${e.token_id}`}
                            onClick={(ev) => {
                              ev.preventDefault();
                              // focus=logs:详情页加载完直接滚到「调用日志」区
                              navigate(`/admin/tokens/${e.token_id}?focus=logs`);
                            }}
                            title="查看该令牌的使用记录"
                            style={{ color: '#1d7dfa', cursor: 'pointer' }}
                          >
                            sk-{e.key_masked}
                          </a>
                        ) : ('sk-' + e.key_masked)
                      ) : '—'}
                      {/* 在飞编号:令牌管理页的速度 tooltip 里也带这个编号,两边可逐行对上 */}
                      {e.id != null && (
                        <span style={{ color: '#86909c', marginLeft: 6 }} title="在飞编号(令牌管理页速度列的 tooltip 里也带它)">
                          #{e.id}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 120, color: '#86909c' }}>
                      {e.plan_name || '—'}
                    </td>
                    <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 140, color: '#86909c' }}>
                      {e.channel_name || '—'}
                    </td>
                    <td
                      style={{
                        padding: '6px 8px', textAlign: 'right', whiteSpace: 'nowrap',
                        // 颜色按**本次请求生效的速度比例**:被限速才提示色
                        color: !e.tps ? '#c9cdd4' : factorColor(e.factor),
                      }}
                      title={
                        e.tps
                          ? '实际 ' + fmtTps(e.tps)
                            + (e.upstream_tps ? '(上游 ' + e.upstream_tps.toFixed(1) + ' t/s)' : '')
                            + (e.factor != null && e.factor < 1 ? ' · 限速中' : '')
                            + (e.reasoning_tokens ? ' · 已产出思考 ' + e.reasoning_tokens + ' token' : '')
                            + '\ntoken 数按字符估算,误差约 ±20%'
                          // 「没有速率」有两种原因,别都写成非流式:
                          //   非流式 —— 本来就没有逐段输出
                          //   流式但还没出第一个字 —— 在等上游预填充 / 深度思考
                          : (e.stream
                            ? '流式请求还没出第一个字(在等上游预填充 / 深度思考),暂时测不出速率'
                            : '非流式请求,没有逐段输出,无法测实时速率')
                      }
                    >
                      {fmtTps(e.tps) || '—'}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: '#86909c', whiteSpace: 'nowrap' }}>
                      {fmtElapsed(e.elapsed_ms)}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: '#86909c', whiteSpace: 'nowrap' }}>
                      {e.phase === 'queued'
                        ? <span style={{ color: '#ff7d00' }}>排队中</span>
                        : liveStatusText(e)}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {/* 深度思考阶段:上游在「想」,一个字都还没吐出来。
                          这段时间上游速率很高(整段都在产 token),但用户体感是「卡住」。
                          不标出来的话,面板上那个速度数字会被误读成「上游变慢了」。 */}
                      {e.reasoning_tokens > 0
                        ? <span style={{ color: '#7c5cff' }} title={'上游正在深度思考,已产出 ' + e.reasoning_tokens + ' 个思考 token(这段时间首字还没出来)'}>
                            思考中 {e.reasoning_tokens}
                          </span>
                        : <span style={{ color: '#c9cdd4' }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ChannelUsagePanel />

      {/* 趋势图放在最后:先看当下实时 → 再看各渠道配额 → 最后回看 7 天走势。 */}
      <div style={{ background: '#fff', borderRadius: 10, padding: 16, border: '1px solid #e5e6eb', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>过去 7 天调用趋势</h3>
          <Radio.Group
            type='button'
            size='small'
            value={trendMetric}
            onChange={(e) => setTrendMetric(e.target.value)}
            options={[
              { label: '调用次数', value: 'calls' },
              { label: '消耗 quota', value: 'quota' },
              { label: '成功率', value: 'success_rate' },
            ]}
          />
        </div>
        <TrendBars byDay={byDay || data.by_day} metric={trendMetric} />
      </div>
    </div>
  );
};

export default AdminStats;
